import { Cartesian2, Cartesian3, Color } from "cesium";
import { PointType, } from "../site/arbitrary-line/types";
import { formatNoLabel } from "../site/arbitrary-line/constants";
import { AssetType, CESIUM_PICKABLE_ENTITY, } from "../types";
import { EarthBrainUtils } from "../utilities/earthbrainutility";
import { Site } from "../site/site";
import { CesiumService } from "../services/cesium-services";
/**
 * Generate cross-section points from the control points of a line work:
 * - Uses control points and intermediate points from the line
 * - Interpolates points at regular intervals (interpolationPitch)
 * - Calculates directions based on line segments
 *
 * @throws Error if line has fewer than 2 points
 */
export async function generateTraditionalCrossSectionPoints(line, setting) {
    const linePoints = line.pointsToCreateCrossSection;
    // Validate input
    if (linePoints.length < 2) {
        throw new Error("Lineworks need at least 2 points");
    }
    const crossSectionPoints = [];
    // let remainingDistance = 0;
    let interpolatedPointsCounter = 1;
    let stationDistanceTracker = 0;
    // Process each segment between consecutive line points
    const upDir = Cartesian3.UNIT_Z;
    for (let i = 0; i < linePoints.length - 1; ++i) {
        let incomingDir;
        let outgoingDir;
        let linePointDir;
        // Calculate incoming direction (from previous point)
        if (linePoints[i - 1]) {
            incomingDir = EarthBrainUtils.getDirectionOfPointOnSitePath(linePoints[i - 1].point, linePoints[i].point, upDir);
        }
        // Calculate outgoing direction (to next point)
        if (linePoints[i + 1]) {
            outgoingDir = EarthBrainUtils.getDirectionOfPointOnSitePath(linePoints[i].point, linePoints[i + 1].point, upDir);
        }
        // Determine direction at this point (average of incoming and outgoing if both exist)
        if (incomingDir && outgoingDir) {
            linePointDir = EarthBrainUtils.addSiteDirection(incomingDir, outgoingDir);
        }
        else if (incomingDir) {
            linePointDir = incomingDir;
        }
        else if (outgoingDir) {
            linePointDir = outgoingDir;
        }
        else {
            throw new Error("Cannot determine direction at line point");
        }
        // Use outgoingDir for interpolation in this segment
        if (!outgoingDir) {
            throw new Error("Cannot determine outgoing direction for interpolation");
        }
        crossSectionPoints.push({
            id: `cross-section-${linePoints[i].id}`,
            lineID: line.id,
            pointType: linePoints[i].pointType,
            label: linePoints[i].label,
            leftOutlineData: {
                direction: linePointDir.left,
                distance: setting.leftWidth,
            },
            rightOutlineData: {
                direction: linePointDir.right,
                distance: setting.rightWidth,
            },
            sampleSize: setting.samplingSize,
            position: linePoints[i].point.clone(),
        });
        const distanceBetnPoints = Cartesian2.distance(linePoints[i].point, linePoints[i + 1].point);
        stationDistanceTracker += distanceBetnPoints;
        if (stationDistanceTracker >= setting.interpolationPitch) {
            while (stationDistanceTracker >= setting.interpolationPitch) {
                stationDistanceTracker -= setting.interpolationPitch;
                const ratio = stationDistanceTracker / distanceBetnPoints;
                const interpolatedPoint = Cartesian3.lerp(linePoints[i + 1].point, linePoints[i].point, ratio, new Cartesian3());
                crossSectionPoints.push({
                    id: `${line.id}-ip-${interpolatedPointsCounter}`,
                    lineID: line.id,
                    pointType: PointType.INTERPOLATED_POINT,
                    label: formatNoLabel(interpolatedPointsCounter++),
                    leftOutlineData: {
                        direction: outgoingDir.left,
                        distance: setting.leftWidth,
                    },
                    rightOutlineData: {
                        direction: outgoingDir.right,
                        distance: setting.rightWidth,
                    },
                    sampleSize: setting.samplingSize,
                    position: interpolatedPoint,
                });
            }
        }
    }
    // Add the end point
    const epPoint = linePoints[linePoints.length - 1];
    const epDirection = EarthBrainUtils.getDirectionOfPointOnSitePath(linePoints[linePoints.length - 2].point, linePoints[linePoints.length - 1].point, upDir);
    crossSectionPoints.push({
        id: epPoint.id,
        lineID: line.id,
        pointType: epPoint.pointType,
        label: epPoint.label,
        leftOutlineData: {
            direction: epDirection.left,
            distance: setting.leftWidth,
        },
        rightOutlineData: {
            direction: epDirection.right,
            distance: setting.rightWidth,
        },
        sampleSize: setting.samplingSize,
        position: epPoint.point.clone(),
    });
    const localPoints = crossSectionPoints.map((point) => point.position);
    const wgs84Points = await Site.getInstance().convertPointsToWGS84(localPoints);
    const cesiumCrossSectionPoints = [];
    for (let i = 0; i < crossSectionPoints.length; i++) {
        const point = crossSectionPoints[i];
        const sphereEntity = CesiumService.getInstance().addSphere(wgs84Points[i], Color.BLUE, point.id, {
            assetType: AssetType.ArbitraryLine_CROSS_SECTION,
            pickableType: CESIUM_PICKABLE_ENTITY.ARBITRARY_LINE_CONTROL_POINT,
            entity: { id: point.id },
            owner: line,
        });
        const labelEntity = CesiumService.getInstance().addLabel(wgs84Points[i], point.label, {
            fillColor: Color.fromCssColorString("#FFFFFF"),
        });
        cesiumCrossSectionPoints.push({
            ...point,
            wgs84data: { sphere: sphereEntity, label: labelEntity },
        });
    }
    return cesiumCrossSectionPoints;
}
//# sourceMappingURL=traditional-cross-section-points.js.map